import { FC, useMemo, ChangeEvent, useCallback } from 'react';
import intl from 'react-intl-universal';
import {
  SidePanelFilter,
  Checkbox,
  FormItem,
  Dropdown,
} from '@getsynapse/design-system';
import { PROJECT_STATUS } from 'utils/constants';
import { Option } from 'utils/customTypes';
import useCapacityFilters from '../../hooks/useCapacityFilters/useCapacityFilters';

interface SidePanelFiltersProps {
  isOpen: boolean;
  onClose: () => void;
}

const SidePanelFilters: FC<SidePanelFiltersProps> = ({ isOpen, onClose }) => {
  const {
    draftFilters,
    filtersCount,
    updateStatusFilter,
    updateCheckboxFilter,
    canApplyFilters,
    applyFilters,
    clearFilters,
  } = useCapacityFilters();

  const statusOptions = useMemo(() => {
    return Object.keys(PROJECT_STATUS).map((key) => ({
      label: intl.get(`PROJECT_DETAIL.STATUS_OPTIONS.${key}`),
      value: PROJECT_STATUS[key],
    }));
  }, []);

  const handleUpdateStatusFilter = useCallback(
    (options: Option[]) => {
      const values = options.map((option: Option) => option.value);
      updateStatusFilter(values);
    },
    [updateStatusFilter]
  );

  const selectedStatusFilter = useMemo(() => {
    if (!draftFilters?.status) {
      return [];
    }
    return statusOptions.filter((option: Option) =>
      draftFilters.status.includes(option.value)
    );
  }, [draftFilters, statusOptions]);

  return (
    <SidePanelFilter
      open={isOpen}
      onClose={onClose}
      onApplyFilters={applyFilters}
      onClearAllFilters={clearFilters}
      applyButtonLabel={intl.get('FILTER_PANEL.APPLY')}
      clearAllButtonLabel={intl.get('FILTER_PANEL.CLEAR_ALL')}
      appliedFiltersCount={filtersCount}
      canApplyFilters={canApplyFilters}
    >
      <div className='flex flex-col gap-4 mt-4'>
        <FormItem>
          <Checkbox
            label={intl.get(
              'TEAMS_CAPACITY.SIDE_PANEL_FILTERS.DEACTIVATED_USERS'
            )}
            checked={draftFilters?.deactivatedUsers ? true : false}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              updateCheckboxFilter(
                'deactivatedUsers',
                event.target.checked ? true : undefined
              );
            }}
          />
        </FormItem>
        <FormItem>
          <Checkbox
            label={intl.get(
              'TEAMS_CAPACITY.SIDE_PANEL_FILTERS.ARCHIVED_PROJECTS'
            )}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              updateCheckboxFilter(
                'archivedProjects',
                event.target.checked ? true : undefined
              );
            }}
            checked={draftFilters?.archivedProjects ? true : false}
          />
        </FormItem>
        <FormItem label={intl.get('PROJECT_DETAIL.STATUS')}>
          <Dropdown
            options={statusOptions}
            values={selectedStatusFilter}
            onChange={handleUpdateStatusFilter}
            multiple
          />
        </FormItem>
      </div>
    </SidePanelFilter>
  );
};

export default SidePanelFilters;
